import NotificationService from 'services/Notification'
import { stringToDate } from 'utils/dateUtils'
import { GroupData, GroupNotificationItem } from '../GroupActionPlan/helpers'

export const isGroupLondonProtocolOptional = (groupData?: GroupData): boolean => {
    return true
}

/** Garante chave de lookup única: o grupo pode trazer sys_id string ou { value, display_value }. */
export const getGroupNotificationSysId = (n?: GroupNotificationItem | Record<string, any>): string => {
    if (!n) return ''
    const s = n.sys_id as string | { value?: string } | undefined
    if (s == null) return ''
    if (typeof s === 'string' && s.trim() !== '') return s.trim()
    if (typeof s === 'object' && typeof s.value === 'string' && s.value.trim() !== '') return s.value.trim()
    return ''
}

/** Usado para decidir create vs update batch: aceita sys_id em string (API) ou ref SN { value } */
export const getLondonProtocolRecordId = (
    londonProtocol?: Record<string, any> | null
): string | undefined => {
    if (!londonProtocol) return undefined
    const s = londonProtocol.sys_id
    if (s == null || s === '') return undefined
    if (typeof s === 'string' && s.trim() !== '') return s
    if (typeof s === 'object' && s !== null && 'value' in s) {
        const v = (s as { value?: string }).value
        if (v && v.trim() !== '') return v
    }
    return undefined
}

/**
 * Converte a data do protocolo (string dd/MM, ISO ou ref SN) para o CustomDatePicker.
 * Evita Invalid Date ao formatar (RangeError) quando a API manda objeto ou ISO.
 */
export const parseLondonProtocolDateToFormDate = (raw: unknown): Date | null => {
    if (raw == null || raw === '') return null
    if (raw instanceof Date) {
        return isNaN(raw.getTime()) ? null : raw
    }
    if (typeof raw === 'object' && raw !== null) {
        const o = raw as { display_value?: string; value?: string }
        if (typeof o.display_value === 'string' && o.display_value.trim() !== '') {
            const d = stringToDate(o.display_value, 'dd/MM/yyyy', '/')
            if (d && !isNaN(d.getTime())) return d
        }
        if (typeof o.value === 'string' && o.value.trim() !== '') {
            return parseYmdStringToDate(o.value)
        }
        return null
    }
    if (typeof raw === 'string') {
        if (raw.includes('/')) {
            const d = stringToDate(raw, 'dd/MM/yyyy', '/')
            if (d && !isNaN(d.getTime())) return d
        }
        return parseYmdStringToDate(raw)
    }
    return null
}

function parseYmdStringToDate(s: string): Date | null {
    const ymd = s.split(/[T ]/)[0] ?? ''
    if (!/^\d{4}-\d{2}-\d{2}/.test(ymd)) return null
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10))
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null
    const date = new Date(y, m - 1, d)
    return isNaN(date.getTime()) ? null : date
}

/** Alinha string/booleano que a API do SN pode retornar em do_not_perform_optional */
export const isLondonDoNotPerformOptionalFromProtocol = (
    londonProtocol?: Record<string, any> | null
): boolean => {
    const v = londonProtocol?.do_not_perform_optional
    return v === 'true' || v === true
}

/**
 * Busca o protocolo de cada notificação (em paralelo) e monta
 * notificacao_sys_id -> protocolo_sys_id para o batch de update.
 */
export const buildLondonProtocolSysIdByNotificationMap = async (
    groupData: GroupData
): Promise<Map<string, string>> => {
    const map = new Map<string, string>()
    const notifications = groupData?.notifications || []
    await Promise.all(
        notifications.map(async (notification) => {
            const notifId = getGroupNotificationSysId(notification)
            if (!notifId) return
            const row = await NotificationService.getLondonProtocolRowByNotificationId(notifId)
            const id = getLondonProtocolRecordId(row)
            if (id) map.set(notifId, id)
        })
    )
    return map
}

/** Garante a 1ª notificação no mapa (sys_id vindo do Redux) se o GET em paralelo ainda vazio. */
export const mergeFirstLondonProtocolIdFromRedux = (
    groupData: GroupData,
    map: Map<string, string>,
    londonFromRedux?: Record<string, any> | null
): Map<string, string> => {
    const firstN = groupData?.notifications?.[0]
    const notifId = getGroupNotificationSysId(firstN)
    const fromRedux = getLondonProtocolRecordId(londonFromRedux)
    if (notifId && fromRedux && !map.has(notifId)) {
        map.set(notifId, fromRedux)
    }
    return map
}

/** Com notificações que já têm registro (GET devolveu sys_id) vs as que ainda precisam de create. */
export const partitionNotificationsByProtocolMap = (
    groupData: GroupData,
    protocolByNotif: Map<string, string>
): { withProtocol: GroupNotificationItem[]; withoutProtocol: GroupNotificationItem[] } => {
    const withProtocol: GroupNotificationItem[] = []
    const withoutProtocol: GroupNotificationItem[] = []
    for (const n of groupData?.notifications || []) {
        const notifId = getGroupNotificationSysId(n)
        if (!notifId) continue
        if (protocolByNotif.get(notifId)) withProtocol.push(n)
        else withoutProtocol.push(n)
    }
    return { withProtocol, withoutProtocol }
}

export const convertLondonNotPerformCreateBatchRequest = (
    groupData: GroupData,
    doNotPerform: boolean
) => {
    return convertLondonNotPerformCreateBatchForNotificationList(
        groupData?.notifications || [],
        doNotPerform
    )
}

export const convertLondonNotPerformCreateBatchForNotificationList = (
    notifications: GroupNotificationItem[] | Array<GroupNotificationItem | Record<string, any>>,
    doNotPerform: boolean
) => {
    const records = (notifications || []).map((notification) => ({
        hospital_incident: getGroupNotificationSysId(notification),
        do_not_perform_optional: doNotPerform
    }))

    return {
        records,
        options: {
            stopOnError: false
        }
    }
}

/**
 * Só notificações que já têm `sys_id` no mapa (o update batch exige sys_id; sem registro, usar create).
 */
export const convertLondonNotPerformUpdateBatchRequest = (
    notifications: GroupNotificationItem[] | Array<GroupNotificationItem | Record<string, any>>,
    londonProtocol: Record<string, any>,
    doNotPerform: boolean,
    protocolSysIdByNotificationId: Map<string, string>
) => {
    const { sys_id: _sysId, hospital_incident: _hospital, ...londonFieldsWithoutId } = londonProtocol
    const records = (notifications || []).map((notification) => {
        const notifId = getGroupNotificationSysId(notification)
        const protocolId = notifId ? protocolSysIdByNotificationId.get(notifId) : undefined
        if (!notifId || !protocolId) {
            throw new Error('Lista de notificacoes com update de Londres deve conter so itens com sys_id de protocolo')
        }
        return {
            ...londonFieldsWithoutId,
            hospital_incident: notifId,
            do_not_perform_optional: doNotPerform,
            sys_id: protocolId
        }
    })

    return {
        records,
        options: {
            stopOnError: false
        }
    }
}
